import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { SectionHeading } from "../components/SectionHeading";
import { Reveal } from "../components/motion";
import { STATS, VALUES, HIGHLIGHTS, COMPANY } from "../lib/content";
import { Seo } from "../components/Seo";

const TIMELINE = [
  { year: "2020", title: "Founded in Southern California", body: "Next Horizon Builders opens its doors with a single crew and a commitment to ground-up multifamily done right." },
  { year: "2021", title: "Self-Perform Concrete", body: "We bring concrete and structural work in-house, taking direct control of the critical path on every build." },
  { year: "2023", title: "A Safety-First Culture", body: "We formalize an uncompromising safety program that protects our crews, partners, and communities on every jobsite." },
  { year: "Today", title: "A Trusted Regional Builder", body: "Now among Southern California's trusted ground-up multifamily builders, delivering with precision, integrity, and long-term value." },
];

const SAFETY_POINTS = [
  "Proper PPE on every jobsite",
  "Compliance with applicable safety standards",
  "Proactive hazard identification",
  "Clean, organized, professional sites",
  "Crew & subcontractor safety awareness",
  "Protecting clients, visitors & the community",
];

const WHY_POINTS = [
  "Experienced, professional project leadership",
  "Reliable schedule & budget management",
  "Preconstruction & design-build expertise",
  "Clear, transparent communication",
  "Meticulous attention to detail",
  "Deep Southern California market knowledge",
];

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <>
      <Seo
        title="About Next Horizon Builders | Southern California Builders"
        description="Learn about Next Horizon Builders, a Southern California general contractor focused on quality, safety, accountability, and ground-up multifamily construction."
        path="/about"
        image="/media/generated/about-hero.jpg"
      />
      <PageHero
        eyebrow="About Us"
        titleLines={["Builders of lasting", "communities."]}
        subtitle="A Southern California general contractor defined by craft, accountability, and a horizon that stretches well beyond a single project."
        image="/media/generated/about-hero.jpg"
        imageAlt="Next Horizon Builders ground-up multifamily project under construction in Southern California"
      />

      {/* Intro */}
      <section className="bg-white py-24 md:py-36">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Our Story"
              title={<>Precision as a<br />first principle.</>}
            />
          </div>
          <div className="lg:col-span-7 space-y-6 text-darkgray/75 text-lg leading-relaxed font-light">
            <Reveal>
              <p>
                Founded in {COMPANY.founded}, Next Horizon Builders was built on a simple conviction: that ground-up
                multifamily construction deserves the same rigor, transparency, and craftsmanship reserved for the
                finest custom work. Years later, that conviction still governs every jobsite we run.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p>
                We self-perform the work that matters most, coordinate the finest trade partners in the region, and
                stake our reputation on delivering communities on time, on budget, and without compromise. From
                preconstruction through final walkthrough, owners work with a single, accountable team.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="text-navy font-normal">
                The result is architecture that performs — assets that elevate neighborhoods and reward the people who
                invest in them for decades to come.
              </p>
            </Reveal>
            <Reveal delay={0.28}>
              <ul className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-3">
                {HIGHLIGHTS.map((h) => (
                  <li key={h} className="flex items-start gap-3 text-base text-darkgray">
                    <span className="text-gold mt-1.5 w-1.5 h-1.5 rounded-full bg-gold shrink-0" />
                    <span className="font-light">{h}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Parallax image band */}
      <section ref={ref} className="relative h-[60vh] overflow-hidden bg-navy">
        <motion.img
          style={{ y }}
          src="/media/generated/about-band.png"
          alt="On site with the Next Horizon Builders team"
          loading="lazy"
          className="absolute inset-0 w-full h-[120%] object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-navy/40" />
        <div className="relative h-full max-w-[1440px] mx-auto px-6 md:px-12 flex items-end pb-16">
          <p className="font-serif font-light italic text-white text-3xl md:text-5xl max-w-3xl leading-tight">
            "We don't just build structures. We build the horizon our communities grow into."
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-white">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-2 lg:grid-cols-4 border-b border-gray-100">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08}>
              <div className={`py-14 md:py-20 ${i !== 0 ? "lg:border-l border-gray-100 lg:pl-10" : ""}`}>
                <div className="font-serif font-light text-2xl md:text-4xl text-navy">{s.value}</div>
                <div className="mt-3 text-[0.7rem] uppercase tracking-[0.2em] text-darkgray/50 font-medium">{s.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="bg-lightgray py-24 md:py-36">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <SectionHeading eyebrow="Our Values" title="What we refuse to compromise." />
          <div className="mt-16 grid md:grid-cols-2 gap-px bg-gray-200">
            {VALUES.map((v, i) => (
              <Reveal key={v.n} delay={(i % 2) * 0.08}>
                <div className="bg-white p-10 md:p-14 h-full">
                  <span className="font-serif text-3xl text-gold">{v.n}</span>
                  <h3 className="mt-6 font-serif font-light text-3xl md:text-4xl text-navy tracking-tight">{v.title}</h3>
                  <p className="mt-4 text-darkgray/70 leading-relaxed font-light">{v.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Safety */}
      <section className="bg-white py-24 md:py-36" data-testid="about-safety">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal>
            <div className="relative overflow-hidden aspect-[4/3]">
              <img
                src="/media/generated/safety.jpg"
                alt="Next Horizon Builders crew during a jobsite safety briefing"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          </Reveal>
          <div>
            <SectionHeading
              eyebrow="Our Commitment"
              title="Safety is built into every decision."
            />
            <div className="mt-6 space-y-5 text-darkgray/75 text-lg leading-relaxed font-light">
              <p>
                Safety is a core value at Next Horizon Builders — not a checklist we complete, but a mindset that
                shapes how we plan, staff, and run every jobsite. We believe the highest-quality work is only possible
                on a site where every person can work safely and with confidence.
              </p>
              <p>
                Our teams follow safe jobsite practices, wear proper PPE, and work in accordance with applicable
                safety standards. We invest in crew and subcontractor awareness, identify hazards before they become
                incidents, and keep our sites clean, organized, and professional — protecting our workers, our
                clients, visitors, and the surrounding community.
              </p>
            </div>
            <div className="mt-8 grid sm:grid-cols-2 gap-x-10 gap-y-4">
              {SAFETY_POINTS.map((p, i) => (
                <Reveal key={p} delay={(i % 2) * 0.06}>
                  <div className="flex items-start gap-3 border-b border-gray-100 pb-3">
                    <Check size={18} className="text-gold shrink-0 mt-1" />
                    <span className="text-darkgray font-light">{p}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Next Horizon Builders */}
      <section className="bg-lightgray py-24 md:py-36" data-testid="about-why">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="order-2 lg:order-1">
            <SectionHeading
              eyebrow="Why Next Horizon Builders"
              title="The builder owners return to."
            />
            <div className="mt-6 space-y-5 text-darkgray/75 text-lg leading-relaxed font-light">
              <p>
                Choosing a general contractor is a decision about trust. Owners partner with Next Horizon Builders for
                our professionalism, our quality of construction, and the reliability of our project management — from
                the first preconstruction estimate to the final walkthrough.
              </p>
              <p>
                We combine disciplined schedule and budget management with design-build coordination and deep Southern
                California market knowledge. Clear communication and meticulous attention to detail mean fewer
                surprises, smoother projects, and long-term relationships built on results.
              </p>
            </div>
            <div className="mt-8 grid sm:grid-cols-2 gap-x-10 gap-y-4">
              {WHY_POINTS.map((p, i) => (
                <Reveal key={p} delay={(i % 2) * 0.06}>
                  <div className="flex items-start gap-3 border-b border-gray-200 pb-3">
                    <Check size={18} className="text-gold shrink-0 mt-1" />
                    <span className="text-darkgray font-light">{p}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal className="order-1 lg:order-2">
            <div className="relative overflow-hidden aspect-[4/3]">
              <img
                src="/media/generated/why-nhb.jpg"
                alt="Next Horizon Builders project leaders on a Southern California jobsite"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-white py-24 md:py-36">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <SectionHeading eyebrow="Our Journey" title="One clear direction, from day one." />
          <div className="mt-16 border-t border-gray-100">
            {TIMELINE.map((t, i) => (
              <Reveal key={t.year} delay={i * 0.06}>
                <div className="grid md:grid-cols-12 gap-6 py-10 md:py-12 border-b border-gray-100 items-baseline">
                  <div className="md:col-span-3 font-serif font-light text-5xl text-gold">{t.year}</div>
                  <h3 className="md:col-span-4 font-serif font-light text-2xl md:text-3xl text-navy">{t.title}</h3>
                  <p className="md:col-span-5 text-darkgray/70 leading-relaxed font-light">{t.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
